/**
 * Champs de la fiche de recueil du besoin employeur (CDC §5.1).
 *
 * La fiche papier sert de référence : demande, commercial, type de contrat,
 * formation, description de l'offre, faisabilité et son motif, candidat
 * pressenti. Le mode de diffusion en a été retiré à la demande du client
 * (ticket du 13/10/2026).
 * Les mêmes champs servent au recueil et à la modification — deux formulaires
 * divergents finiraient par ne plus recueillir la même chose.
 *
 * L'identité de l'employeur (dénomination, SIRET, téléphone, mail) n'est pas
 * ressaisie : elle vient de la fiche entreprise et n'est ici que rappelée.
 */

import { Trash2 } from 'lucide-react';
import type { LigneOffre, TypeContratBesoin } from '../../lib/useEntreprises';
import {
  CLASSE_CHAMP,
  emailValide,
  postesValides,
  type Faisabilite,
  type ValeursFiche,
} from '../../lib/fiche-besoin';

export function Champ({
  libelle,
  children,
  groupe = false,
}: {
  libelle: string;
  children: React.ReactNode;
  /** Plusieurs commandes (boutons, choix) : un `fieldset`, le libellé en légende. */
  groupe?: boolean;
}): JSX.Element {
  // Le libellé enveloppe son champ : il le nomme sans identifiant à poser
  // (RGAA 11.1, TF-341).
  if (groupe) {
    return (
      <fieldset>
        <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          {libelle}
        </legend>
        {children}
      </fieldset>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
    </label>
  );
}

export function ChampsFicheBesoin({
  valeurs,
  onChange,
  formations,
  typesContrat,
  employeur,
}: {
  valeurs: ValeursFiche;
  onChange: (valeurs: ValeursFiche) => void;
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
  /** Rappel de l'employeur, repris de sa fiche : jamais ressaisi ici. */
  employeur?: {
    denomination: string;
    siret: string;
    telephone: string | null;
    email: string | null;
  };
}): JSX.Element {
  const modifier = <Cle extends keyof ValeursFiche>(cle: Cle, valeur: ValeursFiche[Cle]): void =>
    onChange({ ...valeurs, [cle]: valeur });

  const modifierLigne = (index: number, champ: keyof LigneOffre, texte: string): void =>
    modifier(
      'taches',
      valeurs.taches.map((ligne, rang) => (rang === index ? { ...ligne, [champ]: texte } : ligne)),
    );

  const emailCandidatValide = emailValide(valeurs.candidatEmail);

  return (
    <div className="space-y-6">
      {employeur && (
        <Section titre="Employeur">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Rappel libelle="Employeur" valeur={employeur.denomination} />
            <Rappel libelle="SIRET" valeur={employeur.siret} />
            <Rappel libelle="Téléphone" valeur={employeur.telephone ?? 'non renseigné'} />
            <Rappel libelle="Adresse e-mail" valeur={employeur.email ?? 'non renseignée'} />
          </dl>
          <p className="mt-2 text-xs text-codialis-gray">
            Ces coordonnées viennent de la fiche entreprise et se modifient depuis elle.
          </p>
        </Section>
      )}

      <Section titre="Demande">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Date de la demande">
            <input
              type="date"
              value={valeurs.dateDemande}
              onChange={(evenement) => modifier('dateDemande', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Nom du commercial">
            <input
              value={valeurs.commercial}
              onChange={(evenement) => modifier('commercial', evenement.target.value)}
              maxLength={120}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
      </Section>

      <Section titre="Type de contrat">
        <div className="flex flex-wrap gap-2">
          {typesContrat.map((type) => {
            const actif = valeurs.typeContrat === type.valeur;
            return (
              <button
                key={type.valeur}
                type="button"
                aria-pressed={actif}
                onClick={() => modifier('typeContrat', actif ? '' : type.valeur)}
                className={`min-h-[44px] rounded-xl border px-4 py-2 text-sm font-bold transition ${
                  actif
                    ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                    : 'border-gray-200 bg-gray-50 text-codialis-gray'
                }`}
              >
                {type.libelle}
              </button>
            );
          })}
        </div>
      </Section>

      <Section titre="Formation et poste">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Postes">
            <input
              type="number"
              min={1}
              max={200}
              value={valeurs.postes}
              onChange={(evenement) => modifier('postes', evenement.target.value)}
              aria-invalid={!postesValides(valeurs.postes)}
              className={CLASSE_CHAMP}
            />
            {!postesValides(valeurs.postes) && (
              <p className="mt-1 text-xs text-red-600">Indiquer de 1 à 200 postes.</p>
            )}
          </Champ>
          <Champ libelle="Démarrage souhaité">
            <input
              type="date"
              value={valeurs.dateSouhaitee}
              onChange={(evenement) => modifier('dateSouhaitee', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <Champ libelle="Formation visée">
          <select
            value={valeurs.formationCode}
            onChange={(evenement) => modifier('formationCode', evenement.target.value)}
            className={CLASSE_CHAMP}
          >
            <option value="">À définir</option>
            {formations.map((formation) => (
              <option key={formation.code} value={formation.code}>
                {formation.titre}
              </option>
            ))}
          </select>
        </Champ>
      </Section>

      <Section
        titre="Description de l'offre"
        aide="Tâches du référentiel RNCP en regard des tâches confiées à l'apprenant."
      >
        {/* Empilé sur mobile, deux colonnes dès la tablette : un tableau à deux
            colonnes de texte libre devient illisible sous 640 px. */}
        <div className="space-y-3">
          {valeurs.taches.map((ligne, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
              <Champ libelle={`Tâche RNCP ${index + 1}`}>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={ligne.rncp}
                  onChange={(evenement) => modifierLigne(index, 'rncp', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
              <Champ libelle="Tâche confiée à l'apprenant">
                <textarea
                  rows={2}
                  maxLength={500}
                  value={ligne.taches}
                  onChange={(evenement) => modifierLigne(index, 'taches', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
              <button
                type="button"
                onClick={() =>
                  modifier(
                    'taches',
                    valeurs.taches.filter((_, rang) => rang !== index),
                  )
                }
                disabled={valeurs.taches.length <= 1}
                aria-label={`Retirer la ligne ${index + 1}`}
                className="mt-1 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-gray-200 text-codialis-gray transition hover:bg-gray-50 disabled:opacity-40 sm:mt-6"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => modifier('taches', [...valeurs.taches, { rncp: '', taches: '' }])}
          disabled={valeurs.taches.length >= 20}
          className="min-h-[44px] rounded-xl border border-gray-200 px-3 py-2 text-sm font-bold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-40"
        >
          + Ajouter une ligne
        </button>
      </Section>

      <Section titre="Étude de faisabilité" aide="Entre le besoin exprimé et la demande.">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { valeur: 'oui', libelle: 'Oui' },
              { valeur: 'non', libelle: 'Non' },
            ] as { valeur: Faisabilite; libelle: string }[]
          ).map((choix) => {
            const actif = valeurs.faisabilite === choix.valeur;
            return (
              <button
                key={choix.valeur}
                type="button"
                aria-pressed={actif}
                onClick={() => modifier('faisabilite', actif ? '' : choix.valeur)}
                className={`min-h-[44px] rounded-xl border px-5 py-2 text-sm font-bold transition ${
                  actif
                    ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                    : 'border-gray-200 bg-gray-50 text-codialis-gray'
                }`}
              >
                {choix.libelle}
              </button>
            );
          })}
        </div>
        {/* Facultatif, quelle que soit la réponse : il dit pourquoi le besoin
            et la demande se rejoignent, ou non. */}
        <Champ libelle="Motif">
          <textarea
            rows={2}
            maxLength={1000}
            value={valeurs.motifFaisabilite}
            onChange={(evenement) => modifier('motifFaisabilite', evenement.target.value)}
            placeholder="Candidats disponibles, délai, niveau attendu…"
            className={CLASSE_CHAMP}
          />
        </Champ>
      </Section>

      <Section titre="Profil recherché">
        <Champ libelle="Profil et attentes">
          <textarea
            rows={4}
            maxLength={2000}
            value={valeurs.profilRecherche}
            onChange={(evenement) => modifier('profilRecherche', evenement.target.value)}
            placeholder="Missions, permis, disponibilité…"
            className={CLASSE_CHAMP}
          />
        </Champ>
        <label className="flex items-center gap-2 py-1 text-sm text-codialis-navy">
          <input
            type="checkbox"
            checked={valeurs.rqth}
            onChange={(evenement) => modifier('rqth', evenement.target.checked)}
            className="h-5 w-5 rounded"
          />
          Ouvert aux profils RQTH
        </label>
      </Section>

      <Section
        titre="Candidat correspondant"
        aide="Candidat déjà pressenti par le commercial, s'il y en a un."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Nom et prénom">
            <input
              value={valeurs.candidatNom}
              onChange={(evenement) => modifier('candidatNom', evenement.target.value)}
              maxLength={120}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Téléphone">
            <input
              type="tel"
              inputMode="tel"
              value={valeurs.candidatTelephone}
              onChange={(evenement) => modifier('candidatTelephone', evenement.target.value)}
              maxLength={30}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Adresse e-mail">
            <input
              type="email"
              value={valeurs.candidatEmail}
              onChange={(evenement) => modifier('candidatEmail', evenement.target.value)}
              aria-invalid={!emailCandidatValide}
              className={CLASSE_CHAMP}
            />
            {!emailCandidatValide && (
              <p className="mt-1 text-xs text-red-600">Adresse e-mail invalide.</p>
            )}
          </Champ>
        </div>
      </Section>
    </div>
  );
}

function Section({
  titre,
  aide,
  children,
}: {
  titre: string;
  aide?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="font-display text-sm font-bold text-codialis-navy">{titre}</h3>
        {aide && <p className="text-xs text-codialis-gray">{aide}</p>}
      </div>
      {children}
    </section>
  );
}

function Rappel({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </dt>
      <dd className="mt-0.5 text-sm text-codialis-gray">{valeur}</dd>
    </div>
  );
}
